import axios, { AxiosError } from 'axios';
import { useState, ReactNode } from 'react';
import { QueryCache, QueryClient, MutationCache, QueryClientProvider } from 'react-query';

interface ErrorData {
  requestId?: string;
  message: string;
}

interface QueryClientWrapperProps {
  children?: ReactNode;
  onError?: (errorData: ErrorData) => void;
}

export const QueryClientWrapper = ({
  children,
  onError: onErrorCallback,
}: QueryClientWrapperProps) => {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        queryCache: new QueryCache({
          onError: (error, query) => {
            // Requests aborted by the auth interceptor during logout aren't real errors.
            if (axios.isCancel(error)) return;
            if (onErrorCallback) {
              const description =
                (query?.options?.meta?.description as string) || 'carry out unknown operation';
              onErrorCallback(getErrorData(error, description));
            }
          },
        }),
        mutationCache: new MutationCache({
          onError: (error, variables, context, mutation) => {
            if (axios.isCancel(error)) return;
            if (onErrorCallback) {
              const description =
                (mutation?.options?.meta?.description as string) || 'carry out unknown operation';
              onErrorCallback(getErrorData(error, description));
            }
          },
        }),
      })
  );

  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
};

function getErrorData(error: any, action: string): ErrorData {
  // HTTP Errors
  if (error instanceof AxiosError) {
    const status = error.response?.status;
    let message: string;
    switch (status) {
      case 400:
      case 409: {
        const errorData = error.response?.data;
        const errorMessage = errorData?.message || errorData?.error || JSON.stringify(errorData);
        message = `Failed to ${action}: ${errorMessage}.`;
        break;
      }
      case 401: {
        message = `Authentication failed when trying to ${action}. Please sign in again.`;
        break;
      }
      case 403: {
        message = `You don't have permission to ${action}.`;
        break;
      }
      case 404: {
        message = `Failed to ${action}: resource not found.`;
        break;
      }
      case 500: {
        message = `A server error occurred while trying to ${action}. Please try again later.`;
        break;
      }
      default:
        message = `Failed to ${action}: ${error.message || 'Something went wrong'}.`;
        break;
    }
    const requestId = error.config?.headers?.['AC-Request-Id'];
    console.error(
      `Axios error while trying to ${action}.`,
      `Request ID: ${requestId}`
    );
    return { requestId, message };
  }

  // Unknown network related error
  if (error.message?.includes('Network Error')) {
    console.error(`Network error while trying to ${action}.`,);
    return {
      message: `Network error while trying to ${action}. Please check your internet connection.`,
    };
  }

  // Edge case errors
  console.error(`An undocumented error occurred while trying to ${action}.`);
  return {
    message: `Failed to ${action}: ${error.message || 'An unexpected error occurred.'}`,
  };
}
